import React, { useState, useEffect, useCallback } from "react";
import {
  IconX,
  IconPhone,
  IconMail,
  IconBuilding,
  IconEdit,
  IconPlus,
  IconCheck,
  IconNote,
  IconTrash,
  IconPin,
  IconUserCheck,
  IconCrown,
  IconArrowsExchange,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import { useCRM } from "../CRMContext";
import { getContact, updateContact, getContactNotes, createContactNote, deleteContactNote, pinContactNote, getContactActivities, convertContactToCustomer } from "../services/contacts.service";
import { completeActivity } from "../services/activities.service";
import { formatPhoneNumber } from "../../../../helpers/Helpers";
import Select from "react-select";
import Timeline from "./Timeline";
import ActivityForm from "./ActivityForm";
import { ColorPicker, ContactLabel } from "../Label";
import { getRandomColor } from "../label-utils";
import { ACQUISITION_CHANNEL_OPTIONS } from "../constants";
import TransferPipelineModal from "./TransferPipelineModal";
import ContactTagsInline from "./ContactTagsInline";

// react-select usa { value, label }; nossas options já estão nesse formato.
// Drawer do CRM usa z-index 100 — menu portalizado precisa de zIndex maior pra
// aparecer sobre ele (e a chave que importa é `menuPortal`, não `menu`).
const acquisitionSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 14,
    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    "&:hover": { borderColor: "#94a3b8" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, zIndex: 10000 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#f1f5f9"
        : "transparent",
    color: state.isSelected ? "#fff" : "#0f172a",
  }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

const PRIORITY_MAP = {
  low:    { label: "Baixa",  badge: "crm-badge crm-badge--info" },
  medium: { label: "Média",  badge: "crm-badge crm-badge--warning" },
  high:   { label: "Alta",   badge: "crm-badge crm-badge--danger" },
};

const TEMPERATURE_MAP = {
  cold: { label: "Frio",    badge: "crm-badge crm-badge--info" },
  warm: { label: "Morno",   badge: "crm-badge crm-badge--warning" },
  hot:  { label: "Quente",  badge: "crm-badge crm-badge--danger" },
};

const TABS = ["Resumo", "Timeline", "Atividades", "Notas"];

const ContactDrawer = () => {
  const { state, closeDrawer, updateContactInColumn, showToast, refreshBoard } = useCRM();
  const { drawerOpen, activeContactId } = state;

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Resumo");
  const [editing, setEditing] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Notes
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  // Activities
  const [activities, setActivities] = useState([]);
  const [showActivityForm, setShowActivityForm] = useState(false);

  // Editable fields
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editDealValue, setEditDealValue] = useState("");
  const [editPriority, setEditPriority] = useState("");
  const [editTemperature, setEditTemperature] = useState("");
  const [editOwner, setEditOwner] = useState("");
  const [editExpectedCloseDate, setEditExpectedCloseDate] = useState("");
  const [editObservations, setEditObservations] = useState("");
  const [editAcquisitionChannel, setEditAcquisitionChannel] = useState("");
  const [editLabelName, setEditLabelName] = useState("");
  const [editLabelColor, setEditLabelColor] = useState(getRandomColor());

  const fetchContact = useCallback(async () => {
    if (!activeContactId) return;
    setLoading(true);
    try {
      const data = await getContact(activeContactId);
      setContact(data);
      setEditName(data.name || "");
      setEditPhone(data.phone || "");
      setEditEmail(data.email || "");
      setEditCompany(data.company || "");
      setEditPosition(data.position || "");
      setEditDealValue(data.deal_value || "");
      setEditPriority(data.priority || "");
      setEditTemperature(data.temperature || "");
      setEditOwner(data.owner ? String(data.owner) : "");
      setEditExpectedCloseDate(data.expected_close_date || "");
      setEditObservations(data.observations || "");
      setEditAcquisitionChannel(data.acquisition_channel || "");
      if (data.label) {
        setEditLabelName(data.label.name || "");
        setEditLabelColor(data.label.color || getRandomColor());
      } else {
        setEditLabelName("");
        setEditLabelColor(getRandomColor());
      }
    } catch {
      showToast("Erro ao carregar contato.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeContactId, showToast]);

  const fetchNotes = useCallback(async () => {
    if (!activeContactId) return;
    try {
      const resp = await getContactNotes(activeContactId);
      setNotes(resp.results || []);
    } catch { /* silent */ }
  }, [activeContactId]);

  const fetchActivities = useCallback(async () => {
    if (!activeContactId) return;
    try {
      const resp = await getContactActivities(activeContactId);
      setActivities(resp.results || []);
    } catch { /* silent */ }
  }, [activeContactId]);

  useEffect(() => {
    if (drawerOpen && activeContactId) {
      fetchContact();
      fetchNotes();
      fetchActivities();
      setActiveTab("Resumo");
      setEditing(false);
    }
  }, [drawerOpen, activeContactId, fetchContact, fetchNotes, fetchActivities]);

  const handleSaveEdit = async () => {
    // Validação local: name é obrigatório no model.
    // Em vez de bloquear silenciosamente, avisa e impede submit.
    const trimmedName = (editName || "").trim();
    if (!trimmedName) {
      showToast("O nome do contato não pode ficar vazio.", "error");
      return;
    }

    try {
      const data = {
        name: trimmedName,
        // Strings vazias viram undefined → não vão no PATCH → backend mantém valor anterior.
        phone: editPhone || undefined,
        email: editEmail || undefined,
        company: editCompany || undefined,
        position: editPosition || undefined,
        deal_value: editDealValue ? Number(editDealValue) : undefined,
        // priority/temperature precisam ir como null quando "Sem prioridade"/"Sem temperatura"
        // for selecionado, senão o badge no kanban fica preso no valor anterior.
        priority: editPriority || null,
        temperature: editTemperature || null,
        owner: editOwner ? Number(editOwner) : undefined,
        expected_close_date: editExpectedCloseDate || undefined,
        observations: editObservations || undefined,
        // canal pode ser "limpo" pra null explicitamente
        acquisition_channel: editAcquisitionChannel || null,
        label: editLabelName.trim() ? { name: editLabelName.trim(), color: editLabelColor } : null,
      };
      const updated = await updateContact(contact.id, data);
      setContact(updated);
      updateContactInColumn(updated.column, updated);
      setEditing(false);
      showToast("Contato atualizado!");
    } catch (err) {
      // Mostra mensagem específica do backend quando disponível
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        Object.values(err?.response?.data || {})[0];
      const msg = Array.isArray(detail) ? detail[0] : (typeof detail === "string" ? detail : "Erro ao atualizar.");
      showToast(msg, "error");
    }
  };

  const handleConvertToCustomer = async () => {
    if (!contact) return;
    if (contact.is_converted || contact.customer) {
      showToast("Este contato já é cliente.", "info");
      return;
    }
    if (!contact.phone) {
      Swal.fire({
        icon: "warning",
        title: "Telefone obrigatório",
        text: "Adicione um telefone ao contato antes de transformar em cliente.",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Transformar em cliente?",
      html: `
        <p style="margin: 0 0 8px;">Será criado um registro de cliente com:</p>
        <ul style="text-align: left; margin: 0; padding-left: 20px; font-size: 14px;">
          <li><strong>Nome:</strong> ${contact.name || "—"}</li>
          <li><strong>Telefone:</strong> ${contact.phone}</li>
          ${contact.email ? `<li><strong>E-mail:</strong> ${contact.email}</li>` : ""}
        </ul>
        <p style="margin: 12px 0 0; font-size: 12px; color: #64748b;">Esta ação contabiliza como conversão nas métricas.</p>
      `,
      showCancelButton: true,
      confirmButtonText: "Confirmar conversão",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
    });
    if (!result.isConfirmed) return;

    try {
      const resp = await convertContactToCustomer(contact.id);
      if (resp?.success) {
        showToast("Contato convertido em cliente!", "success");
        await fetchContact();
        if (refreshBoard) refreshBoard();
      }
    } catch (err) {
      const code = err?.response?.data?.error_code;
      if (code === "ALREADY_CONVERTED") {
        showToast("Este contato já é cliente.", "info");
        await fetchContact();
      } else if (code === "MISSING_PHONE") {
        Swal.fire({ icon: "warning", title: "Telefone obrigatório",
          text: "Adicione um telefone antes de converter.",
          confirmButtonColor: "#3b82f6" });
      } else {
        Swal.fire({ icon: "error", title: "Erro",
          text: err?.response?.data?.error || "Não foi possível converter o contato.",
          confirmButtonColor: "#3b82f6" });
      }
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await createContactNote(activeContactId, { content: newNote.trim(), note_type: "general" });
      setNewNote("");
      fetchNotes();
      showToast("Nota adicionada!");
    } catch {
      showToast("Erro ao adicionar nota.", "error");
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteContactNote(activeContactId, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      showToast("Nota removida!");
    } catch {
      showToast("Erro ao remover nota.", "error");
    }
  };

  const handlePinNote = async (noteId) => {
    try {
      await pinContactNote(activeContactId, noteId);
      fetchNotes();
    } catch { /* silent */ }
  };

  const handleCompleteActivity = async (actId) => {
    try {
      await completeActivity(actId);
      fetchActivities();
      showToast("Atividade concluída!");
    } catch {
      showToast("Erro ao concluir atividade.", "error");
    }
  };

  return (
    <div className={`crm-drawer ${drawerOpen ? "crm-drawer--open" : ""}`}>
      <div className="crm-drawer__backdrop" onClick={closeDrawer} />
      <div className="crm-drawer__panel">
        {loading || !contact ? (
          <div className="crm-loading">Carregando...</div>
        ) : (
          <>
            {/* Header */}
            <div className="crm-drawer__header">
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>
                    {contact.name || contact.phone}
                  </h3>
                  {(contact.is_converted || contact.customer) && (
                    <span
                      title={
                        contact.customer_data?.created_at
                          ? `Cliente desde ${new Date(contact.customer_data.created_at).toLocaleDateString("pt-BR")}`
                          : "Convertido em cliente"
                      }
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 8px", borderRadius: 999,
                        background: "#dcfce7", color: "#166534",
                        fontSize: 11, fontWeight: 600,
                      }}
                    >
                      <IconCrown size={12} /> Cliente
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: "var(--text-muted)" }}>
                  {contact.phone && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <IconPhone size={14} /> {formatPhoneNumber(contact.phone) || contact.phone}
                    </span>
                  )}
                  {contact.email && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <IconMail size={14} /> {contact.email}
                    </span>
                  )}
                  {contact.company && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <IconBuilding size={14} /> {contact.company}
                    </span>
                  )}
                </div>
                {/* Tags — inline com sugestões preset do pipeline */}
                <ContactTagsInline
                  contact={contact}
                  onChange={(updated) => {
                    setContact(updated);
                    // Propaga pro estado global do board pra atualizar o card
                    // sem precisar F5. updated.column pode ser id (número) ou
                    // o objeto inline; o reducer espera o id.
                    const columnId = updated?.column?.id || updated?.column;
                    if (columnId) {
                      updateContactInColumn(columnId, updated);
                    }
                  }}
                />
                {/* CTA: transformar em cliente */}
                {!contact.is_converted && !contact.customer && (
                  <button
                    type="button"
                    className="crm-btn crm-btn--sm"
                    onClick={handleConvertToCustomer}
                    style={{
                      marginTop: 10,
                      background: "#16a34a", color: "#fff", border: "none",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <IconUserCheck size={14} /> Transformar em Cliente
                  </button>
                )}
                <button
                  type="button"
                  className="crm-btn crm-btn--sm"
                  onClick={() => setShowTransferModal(true)}
                  style={{
                    marginTop: 10, marginLeft: 6,
                    background: "#3b82f6", color: "#fff", border: "none",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                  title="Transferir contato para outro pipeline"
                >
                  <IconArrowsExchange size={14} /> Transferir pipeline
                </button>
              </div>
              <button className="crm-drawer__close" onClick={closeDrawer}>
                <IconX size={20} />
              </button>
            </div>

            {/* Stats */}
            <div className="crm-drawer__stats-row" style={{ padding: "12px 24px" }}>
              <div className="crm-drawer__stat">
                <div className="crm-drawer__stat-value">{formatCurrency(contact.deal_value)}</div>
                <div className="crm-drawer__stat-label">Valor</div>
              </div>
              <div className="crm-drawer__stat">
                <div className="crm-drawer__stat-value">{contact.owner_name || "—"}</div>
                <div className="crm-drawer__stat-label">Responsável</div>
              </div>
              <div className="crm-drawer__stat">
                <div className="crm-drawer__stat-value">{contact.days_in_stage ?? "—"}d</div>
                <div className="crm-drawer__stat-label">Dias no Estágio</div>
              </div>
              <div className="crm-drawer__stat">
                <div className="crm-drawer__stat-value">{formatDate(contact.created_at)}</div>
                <div className="crm-drawer__stat-label">Criado</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="crm-drawer__tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`crm-drawer__tab ${activeTab === tab ? "crm-drawer__tab--active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="crm-drawer__content">
              {/* RESUMO */}
              {activeTab === "Resumo" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    {editing ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="crm-btn crm-btn--sm" onClick={() => setEditing(false)}>Cancelar</button>
                        <button className="crm-btn crm-btn--sm crm-btn--primary" onClick={handleSaveEdit}>Salvar</button>
                      </div>
                    ) : (
                      <button className="crm-btn crm-btn--sm" onClick={() => setEditing(true)}>
                        <IconEdit size={14} /> Editar
                      </button>
                    )}
                  </div>

                  {editing ? (
                    <div>
                      <div className="crm-form-group">
                        <label className="crm-label">Nome *</label>
                        <input className="crm-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Telefone</label>
                        <input className="crm-input" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="(00) 00000-0000" />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">E-mail</label>
                        <input className="crm-input" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Empresa</label>
                        <input className="crm-input" value={editCompany} onChange={(e) => setEditCompany(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Cargo</label>
                        <input className="crm-input" value={editPosition} onChange={(e) => setEditPosition(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Valor do Negócio</label>
                        <input type="number" className="crm-input" value={editDealValue} onChange={(e) => setEditDealValue(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Prioridade</label>
                        <select className="crm-select" value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                          <option value="">Sem prioridade</option>
                          <option value="low">Baixa</option>
                          <option value="medium">Média</option>
                          <option value="high">Alta</option>
                        </select>
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Temperatura</label>
                        <select className="crm-select" value={editTemperature} onChange={(e) => setEditTemperature(e.target.value)}>
                          <option value="">Sem temperatura</option>
                          <option value="cold">Frio</option>
                          <option value="warm">Morno</option>
                          <option value="hot">Quente</option>
                        </select>
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Responsável</label>
                        <select className="crm-select" value={editOwner} onChange={(e) => setEditOwner(e.target.value)}>
                          <option value="">Sem responsável</option>
                          {state.teamMembers.map((m) => (
                            <option key={m.id} value={m.id}>{m.first_name || m.username || m.email}</option>
                          ))}
                        </select>
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Previsão de Fechamento</label>
                        <input type="date" className="crm-input" value={editExpectedCloseDate} onChange={(e) => setEditExpectedCloseDate(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Canal de Aquisição</label>
                        <Select
                          options={ACQUISITION_CHANNEL_OPTIONS}
                          value={
                            ACQUISITION_CHANNEL_OPTIONS.find(
                              (o) => o.value === editAcquisitionChannel,
                            ) || null
                          }
                          onChange={(opt) =>
                            setEditAcquisitionChannel(opt ? opt.value : "")
                          }
                          isClearable
                          placeholder="Digite ou selecione um canal..."
                          noOptionsMessage={() => "Nenhum canal encontrado"}
                          styles={acquisitionSelectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Observações</label>
                        <textarea className="crm-textarea" rows={3} value={editObservations} onChange={(e) => setEditObservations(e.target.value)} />
                      </div>
                      <div className="crm-form-group">
                        <label className="crm-label">Etiqueta</label>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            className="crm-input"
                            style={{ maxWidth: 200 }}
                            placeholder="Nome da etiqueta"
                            value={editLabelName}
                            onChange={(e) => setEditLabelName(e.target.value)}
                          />
                          {editLabelName && (
                            <button type="button" className="crm-btn crm-btn--sm" onClick={() => setEditLabelName("")}>
                              <IconX size={14} />
                            </button>
                          )}
                        </div>
                        {editLabelName && (
                          <div style={{ marginTop: 8 }}>
                            <ColorPicker selectedColor={editLabelColor} onChange={setEditLabelColor} />
                            <div style={{ marginTop: 6 }}>
                              <ContactLabel label={{ name: editLabelName, color: editLabelColor }} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Telefone</div>
                        <div className="crm-drawer__field-value">{formatPhoneNumber(contact.phone) || contact.phone || "—"}</div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">E-mail</div>
                        <div className="crm-drawer__field-value">{contact.email || "—"}</div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Empresa</div>
                        <div className="crm-drawer__field-value">{contact.company || "—"}</div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Cargo</div>
                        <div className="crm-drawer__field-value">{contact.position || "—"}</div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Prioridade</div>
                        <div className="crm-drawer__field-value">
                          {PRIORITY_MAP[contact.priority]
                            ? <span className={PRIORITY_MAP[contact.priority].badge}>{PRIORITY_MAP[contact.priority].label}</span>
                            : contact.priority || "—"}
                        </div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Temperatura</div>
                        <div className="crm-drawer__field-value">
                          {TEMPERATURE_MAP[contact.temperature]
                            ? <span className={TEMPERATURE_MAP[contact.temperature].badge}>{TEMPERATURE_MAP[contact.temperature].label}</span>
                            : contact.temperature || "—"}
                        </div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Previsão de Fechamento</div>
                        <div className="crm-drawer__field-value">{formatDate(contact.expected_close_date)}</div>
                      </div>
                      <div className="crm-drawer__field">
                        <div className="crm-drawer__field-label">Canal de Aquisição</div>
                        <div className="crm-drawer__field-value">
                          {ACQUISITION_CHANNEL_OPTIONS.find(o => o.value === contact.acquisition_channel)?.label || "—"}
                        </div>
                      </div>
                      {contact.observations && (
                        <div className="crm-drawer__field">
                          <div className="crm-drawer__field-label">Observações</div>
                          <div className="crm-drawer__field-value">{contact.observations}</div>
                        </div>
                      )}
                      {contact.conversation_history_ai && (
                        <div className="crm-drawer__field">
                          <div className="crm-drawer__field-label">Resumo da Conversa (IA)</div>
                          <div className="crm-drawer__field-value" style={{ whiteSpace: "pre-wrap" }}>{contact.conversation_history_ai}</div>
                        </div>
                      )}
                      {contact.loss_reason && (
                        <div className="crm-drawer__field">
                          <div className="crm-drawer__field-label">Motivo da Perda</div>
                          <div className="crm-drawer__field-value">{contact.loss_reason}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TIMELINE */}
              {activeTab === "Timeline" && activeContactId && (
                <Timeline contactId={activeContactId} />
              )}

              {/* ATIVIDADES */}
              {activeTab === "Atividades" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                    <button className="crm-btn crm-btn--sm crm-btn--primary" onClick={() => setShowActivityForm(true)}>
                      <IconPlus size={14} /> Nova Atividade
                    </button>
                  </div>

                  {activities.length === 0 ? (
                    <div className="crm-empty"><span className="crm-empty__desc">Nenhuma atividade.</span></div>
                  ) : (
                    activities.map((act) => (
                      <div key={act.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--border-color, #e2e8f0)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>{act.title}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                              {act.activity_type} &middot; {act.scheduled_at ? new Date(act.scheduled_at).toLocaleString("pt-BR") : "Sem data"}
                              {act.assigned_to_name && ` &middot; ${act.assigned_to_name}`}
                            </div>
                          </div>
                          {act.status !== "completed" && (
                            <button className="crm-btn crm-btn--sm" onClick={() => handleCompleteActivity(act.id)} title="Concluir">
                              <IconCheck size={14} />
                            </button>
                          )}
                          {act.status === "completed" && (
                            <span className="crm-badge crm-badge--success">Concluída</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {showActivityForm && (
                    <ActivityForm
                      contactId={activeContactId}
                      pipelineId={state.activePipelineId}
                      onClose={() => setShowActivityForm(false)}
                      onCreated={() => fetchActivities()}
                    />
                  )}
                </div>
              )}

              {/* NOTAS */}
              {activeTab === "Notas" && (
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    <textarea
                      className="crm-textarea"
                      rows={2}
                      placeholder="Escreva uma nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                    />
                    <button className="crm-btn crm-btn--primary crm-btn--sm" onClick={handleAddNote} style={{ alignSelf: "flex-end" }}>
                      Adicionar
                    </button>
                  </div>

                  {notes.length === 0 ? (
                    <div className="crm-empty"><span className="crm-empty__desc">Nenhuma nota.</span></div>
                  ) : (
                    notes.map((note) => (
                      <div key={note.id} style={{ padding: "12px 0", borderBottom: "1px solid var(--border-color, #e2e8f0)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>{note.content}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                              {note.created_by_name} &middot; {new Date(note.created_at).toLocaleString("pt-BR")}
                              {note.is_pinned && " &middot; Fixada"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            <button className="crm-btn crm-btn--sm crm-btn--icon" onClick={() => handlePinNote(note.id)} title={note.is_pinned ? "Desfixar" : "Fixar"}>
                              <IconPin size={14} />
                            </button>
                            <button className="crm-btn crm-btn--sm crm-btn--icon" onClick={() => handleDeleteNote(note.id)} title="Remover">
                              <IconTrash size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <TransferPipelineModal
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        contact={contact}
        onSuccess={(updated) => {
          showToast?.("Contato transferido para outro pipeline.", "success");
          if (refreshBoard) refreshBoard();
          if (updated && setContact) setContact((prev) => ({ ...prev, ...updated }));
        }}
      />
    </div>
  );
};

export default ContactDrawer;
